import AddInput from '@nile/banyan/src/Components/Public/AddInput';
import { BoxContainer } from '@nile/banyan/src/Utils/style';
import {
  AccessControl,
  ActionGroup,
  addInputConstraints,
  CascadeCard,
  getTranslatedText,
  Icon,
  NoBorderWrapper,
  preFormatForm,
  TEXT_LENGTH,
  ticketStatusConstraints,
  UnauthorisedAccess,
  validateForm,
  isAuthorised,
  getCurrentUserHook,
  PermissionToggle,
  NoDataCard
} from '@nile/components';
import { Check, Plus, Times } from '@styled-icons/fa-solid';
import {
  Button,
  Heading,
  InlineEdit,
  Page,
  ResourceItem,
  ResourceList,
  TextContainer,
  TextField,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import React, { useEffect, useState } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import nanoid from 'nanoid';
import styled from 'styled-components';
import { SuperSortable } from '@nile/components/src/SuperSortable/SuperSortable';
import { cloneDeep, isEmpty, sortBy } from 'lodash';
import envConfig from '@nile/home/src/Config/env';

const BoxContainerWrapper = styled(BoxContainer)`
  height: 100%;
  &:hover {
    //transform: scale3d(1.01,1.01,1.01);
  }
  transition: transform 100ms linear;
  .cascadeContainer {
    &:last-child {
      overflow: auto;
    }
  }
  .py-10 {
    padding: 10px 0;
  }
  .cause-list {
    height: calc(100% - 117px);
    overflow: auto;
  }
  .subcause-list {
    height: calc(100% - 178px);
    overflow: auto;
  }
  .w-90 {
    width: 90%;
  }
`;

const ActionIcons = styled.div`
  height: 20px;
  margin-left: auto;
  margin-right: 10px;
`;

const TicketStyled = styled.div`
  .pr-10 {
    padding-right: 10px;
  }
  .overall-padding {
    padding: 10px 20px 10px 10px;
  }
  margin: 0 !important;
  &.edit {
    margin: 0;
    margin-top: 10px !important;
    margin-bottom: 10px !important;
  }
`;

function CauseCascade(props) {
  return (
    <AccessControl
      roleToCheck="customFields.view"
      render={<CauseCascadeSection {...props} />}
      fallback={<UnauthorisedAccess />}
    />
  );
}

const SubcauseInput = (props) => {
  const { intl, onChange, disableSort } = props;
  const [name, setName] = useState(props?.item?.name || '');
  const [description, setDescription] = useState(
    props?.item?.details?.description || ''
  );
  const [errors, setErrors] = useState(props?.errors || null);

  const onSuccessHandler = () => {
    const formObj = { name, description };
    const validationResult = validateForm(
      preFormatForm(formObj),
      ticketStatusConstraints
    );
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    if (props?.mode === 'edit') {
      props.onSuccess({ ...props.item, name, description });
    } else {
      props.onSuccess(props?.selectedCauseItem, { name, description });
    }
    setErrors({});
    setName('');
    setDescription('');
  };

  return (
    <TicketStyled
      className={
        props?.mode === 'edit'
          ? 'fx Ticket-Status-Form edit w100'
          : 'fx Ticket-Status-Form w100'
      }
      style={props?.mode !== 'edit' ? { marginBottom: 20 } : {}}
    >
      <div className="fx actr full-width">
        <div className="flex fcol full-width overall-padding">
          <div className="mb-10">
            <div className="flex actr g10">
              {!disableSort && (
                <AccessControl
                  roleToCheck="customFields.edit"
                  render={<SuperSortable.DragHandle className="self-center" />}
                />
              )}

              <div className="w100">
                <TextField
                  id="ticketStatusName"
                  value={name}
                  name="name"
                  onChange={(val) => {
                    setErrors({});
                    setName(val);
                    if (onChange) {
                      onChange('name', val);
                    }
                  }}
                  placeholder={`${getTranslatedText(
                    intl,
                    props.childrenPlaceholderText
                  )} ${props?.selectedCauseItem?.name}`}
                  type="text"
                  error={errors?.name?.[0]}
                  maxLength={TEXT_LENGTH.size_4}
                />
              </div>
            </div>
          </div>
          <NoBorderWrapper>
            <TextField
              id="ticketStatusDescription"
              value={description}
              name="description"
              onChange={(val) => {
                setErrors({});
                setDescription(val);
                if (onChange) {
                  onChange('description', val);
                }
              }}
              placeholder={getTranslatedText(
                intl,
                'admin.CauseAndSubcause.subcause.pl.2'
              )}
              type="text"
              noborder
              error={errors?.description?.[0]}
              maxLength={TEXT_LENGTH.size_6}
            />
          </NoBorderWrapper>
        </div>
        <div className="fx actr jend mr-20">
          {props?.mode === 'edit' ? (
            <>
              <Button
                size="small"
                onClick={onSuccessHandler}
                icon={<SVGIcon name="Check" color="#00d247" />}
              />
              <div className="ml-10">
                <Button
                  onClick={props?.onClose}
                  size="small"
                  icon={<SVGIcon name="Close" color="#d72c0d" />}
                />
              </div>
            </>
          ) : (
            <div>
              <Button
                size="small"
                onClick={onSuccessHandler}
                plain
                icon={<SVGIcon name="Add" />}
              />
            </div>
          )}
        </div>
      </div>
    </TicketStyled>
  );
};

function CauseCascadeSection(props) {
  const { intl, disableSort, emptyState, isNewFieldTemplate } = props;

  const sortedData = props?.options ? sortBy(props?.options, 'order') : [];

  const [data, setData] = useState(sortedData);
  const dataByRef = React.useRef(null);
  dataByRef.current = data;

  const [selectedCause, setSelectedCause] = useState([]);
  const selectedCauseByRef = React.useRef(null);
  selectedCauseByRef.current = selectedCause;

  const [errors, setErrors] = useState({});
  const [submitActionLoading, setSubmitActionLoading] = useState(false);

  useEffect(() => {
    if (props?.options && sortedData?.[0]) {
      setSelectedCause(sortedData?.[0]);
    }
  }, [props?.options]);

  const isEditAuthorised = props?.isEditAuthorised;

  const handleSave = () => {
    if (props.handleSave) {
      props.handleSave(dataByRef.current);
    }
  };

  const addCause = (el) => {
    const columnName = props?.columnName;
    const formObj = { [columnName]: el };
    const validationResult = validateForm(
      preFormatForm(formObj),
      addInputConstraints(columnName)
    );
    if (validationResult) {
      setErrors(validationResult);
      return;
    }
    setErrors({});
    const addedCause = {
      id: nanoid(),
      name: el,
      value: el,
      children: null,
      details: null,
      order: data?.length ? data?.length : 1
    };
    const updatedData = [...dataByRef.current, addedCause];
    setSelectedCause(addedCause);
    setData(updatedData);
  };

  const deleteCause = (item) => {
    const base = dataByRef.current;
    const updatedData = base.filter((el) => el?.id !== item?.id);

    if (item.id === selectedCause.id) {
      setSelectedCause(updatedData[0]);
    }
    setData(updatedData);
  };

  const updateCause = (item, name, oldName) => {
    const base = [...dataByRef.current];
    const updatedValue = name;
    let dataToUpdate = base.filter((data) => data?.id === item?.id)[0];
    dataToUpdate = {
      ...dataToUpdate,
      name: updatedValue,
      value: updatedValue
    };
    let updatedData = [];
    base.forEach((el) => {
      if (el?.id === item?.id) {
        updatedData = [...updatedData, dataToUpdate];
      } else {
        updatedData = [...updatedData, el];
      }
    });
    setSelectedCause(dataToUpdate);
    setData(updatedData);
  };

  const [subCauseEditData, setSubCauseEditData] = useState(null);

  const onSubCauseChange = (key, val) => {
    const subCauseData = cloneDeep(subCauseEditData);
    const updatedSubCauseItem = subCauseData?.item;
    if (key) {
      const value = val || null;
      updatedSubCauseItem[key] = value;
    }

    setSubCauseEditData({
      ...subCauseEditData,
      item: updatedSubCauseItem
    });
  };

  const onSubCauseEdit = (item, index) => {
    setSubCauseEditData({
      item,
      index,
      original: item
    });
  };

  const updateSubCauseOrder = (reorderedSubCause, parentItem) => {
    const base = [...dataByRef.current];
    const updatedData = [];
    base.forEach((el) => {
      if (el?.id === parentItem?.id) {
        updatedData.push({
          ...el,
          children: reorderedSubCause
        });
      } else {
        updatedData.push(el);
      }
    });
    const updateSelectedData = updatedData.find(
      (el) => el?.id === selectedCauseByRef.current?.id
    );
    setSelectedCause(updateSelectedData);
    setData(updatedData);
  };
  const updateSubCause = (item, parentItem, name, oldName, desc = '') => {
    const base = [...dataByRef.current];
    const editedSubcause = {
      ...item,
      value: name,
      name: name,
      details: {
        description: desc
      }
    };

    const children = parentItem?.children || [];
    let updatedChildren = [];
    children.forEach((el) => {
      if (el?.id === editedSubcause?.id) {
        updatedChildren = [...updatedChildren, editedSubcause];
      } else {
        updatedChildren = [...updatedChildren, el];
      }
    });

    const updatedParent = { ...parentItem, children: updatedChildren };

    let updatedData = [];
    base.forEach((el) => {
      if (el?.id === parentItem?.id) {
        updatedData = [...updatedData, updatedParent];
      } else {
        updatedData = [...updatedData, el];
      }
    });
    const updateSelectedData = updatedData.find(
      (el) => el?.id === selectedCauseByRef.current?.id
    );
    setSelectedCause(updateSelectedData);
    setData(updatedData);
  };

  const deleteSubCause = (parent, item) => {
    const base = [...dataByRef.current];
    const updatedChildren = parent?.children.filter(
      (el) => el?.id !== item?.id
    );
    const updatedParent = { ...parent, children: updatedChildren };

    let updatedData = [];
    base.forEach((el) => {
      if (el?.id === parent?.id) {
        updatedData = [...updatedData, updatedParent];
      } else {
        updatedData = [...updatedData, el];
      }
    });
    const updateSelectedData = updatedData.find(
      (el) => el?.id === selectedCauseByRef.current?.id
    );
    setSelectedCause(updateSelectedData);
    setData(updatedData);
  };

  const addSubCause = (parent, el) => {
    const base = [...dataByRef.current];
    const children = parent?.children || [];
    const addedSubCause = {
      id: nanoid(),
      name: el?.name,
      value: el?.name,
      details: {
        description: el?.description
      },
      order: children?.length ? children?.length + 1 : 1
    };
    const subCauses = [...children, addedSubCause];
    const updatedCause = { ...parent, children: subCauses };

    let updatedData = [];
    base.forEach((item) => {
      if (item?.id === parent?.id) {
        updatedData = [...updatedData, updatedCause];
      } else {
        updatedData = [...updatedData, item];
      }
    });

    const updateSelectedData = updatedData.find(
      (el) => el?.id === selectedCauseByRef.current?.id
    );
    setSelectedCause(updateSelectedData);

    setData(updatedData);
  };

  const changeCause = (item) => {
    setSelectedCause(item);
  };

  const showEmptyState = isEmpty(dataByRef.current) && !isEditAuthorised;
  // Show empty state when there is no edit permission and no data

  return (
    <div className="full-height">
      <Page
        contentHeight="160px"
        variant="primary"
        subtitle={
          <>
            <TextContainer>
              <TextStyle variation="subdued">{props.subtitle}</TextStyle>
            </TextContainer>
            <div className="mt10">{props.featureGuide}</div>
          </>
        }
        title={<TextStyle weight="Bold">{props.title}</TextStyle>}
        primaryAction={
          <ActionGroup
            primaryText={<FormattedMessage id="common.save" />}
            primaryLoading={props.saveLoading}
            primaryAction={handleSave}
            secondaryAction={props.secondaryAction}
            primaryDisabled={!isEditAuthorised}
          />
        }
      >
        <div className="main-content-decider full-height">
          <BoxContainerWrapper>
            <div className={isNewFieldTemplate ? 'mb-20' : 'mb40'}>
              <PermissionToggle
                hideShowToClients
                mandatoryOnCreation={props.mandatoryOnCreation}
                setMandatoryOnCreation={props.setMandatoryOnCreation}
                mandatoryOnClosure={props.mandatoryOnClosure}
                setMandatoryOnClosure={props.setMandatoryOnClosure}
                disableEdit={!isEditAuthorised}
                flexDirection="row"
                showWarning={props.showWarning}
              />
            </div>
            {!showEmptyState ? (
              <CascadeCard className="fx full-width">
                <div
                  className={
                    isNewFieldTemplate
                      ? 'full-width'
                      : 'cascadeContainer bg-white'
                  }
                >
                  <div className="p20">
                    <Heading element="h2">{props.parentTitle}</Heading>
                  </div>
                  <div className="addInputWrap">
                    <AccessControl
                      roleToCheck="customFields.edit"
                      render={
                        <AddInput
                          id="causeinput"
                          update={addCause}
                          errors={errors?.[props?.columnName] || []}
                          placeholder={getTranslatedText(
                            intl,
                            props.parentPlaceholderText
                          )}
                        />
                      }
                    />
                  </div>

                  <div
                    data-qa="cause-list"
                    className={isNewFieldTemplate ? 'mt10' : 'mt10 cause-list'}
                  >
                    <SuperSortable
                      className="sort-wrapper"
                      list={dataByRef.current || []}
                      onDragEnd={(reorderedData) => {
                        const updatedDataWithOrder = reorderedData.map(
                          (field, i) => ({
                            ...field,
                            order: i + 1
                          })
                        );
                        setData(updatedDataWithOrder);
                      }}
                      animation={200}
                    >
                      {dataByRef.current.map((item) => {
                        if (!item) return null;

                        const { id, name } = item;
                        const isSelected = selectedCause?.name === name;
                        const classNames = [
                          'flex g5 Superops-ResourceItem resource-item-wrapper',
                          isSelected &&
                            'Superops-ResourceItem--selected Superops-ResourceItem--selectMode'
                        ]
                          .filter(Boolean)
                          .join(' ');

                        return (
                          <div key={id || name} className={classNames}>
                            <div className="w100">
                              <Cause
                                deleteCause={deleteCause}
                                updateCause={updateCause}
                                item={item}
                                isSelected={isSelected}
                                setSelectedCause={changeCause}
                                disableSort={disableSort}
                              />
                            </div>
                          </div>
                        );
                      })}
                    </SuperSortable>
                  </div>
                </div>
                {!isNewFieldTemplate && (
                  <div className="cascadeContainer bg-white">
                    <div title={'Subcause'} className="full-height">
                      <div className="pb20 mb-10">
                        <Heading element="h2">{props.childTitle}</Heading>
                      </div>
                      <AccessControl
                        roleToCheck="customFields.edit"
                        render={
                          <SubcauseInput
                            intl={intl}
                            selectedCauseItem={selectedCauseByRef.current}
                            onSuccess={addSubCause}
                            childrenPlaceholderText={
                              props.childrenPlaceholderText
                            }
                            disableSort={disableSort}
                          />
                        }
                      />

                      <div
                        data-qa="subcause-list"
                        className="subcause-list mt-10"
                      >
                        <SuperSortable
                          className="sort-wrapper"
                          list={selectedCauseByRef?.current?.children || []}
                          onDragEnd={(reorderedData) => {
                            const reorderedItems = reorderedData.map(
                              (item, idx) => ({ ...item, order: idx + 1 })
                            );
                            updateSubCauseOrder(
                              reorderedItems,
                              selectedCauseByRef?.current
                            );
                          }}
                          animation={200}
                        >
                          {selectedCauseByRef?.current?.children?.map(
                            (item, index) => {
                              const { id, name } = item;
                              return (
                                <SubcauseItem
                                  intl={intl}
                                  deleteCause={deleteSubCause.bind(
                                    this,
                                    selectedCauseByRef.current,
                                    item
                                  )}
                                  onEdit={onSubCauseEdit}
                                  onEditCancel={() => {
                                    setSubCauseEditData(null);
                                  }}
                                  updateSubCause={updateSubCause}
                                  parentItem={selectedCauseByRef.current}
                                  item={item}
                                  childrenPlaceholderText={
                                    props.childrenPlaceholderText
                                  }
                                  index={index}
                                  editItem={subCauseEditData}
                                  onChange={onSubCauseChange}
                                  disableSort={disableSort}
                                />
                              );
                            }
                          )}
                        </SuperSortable>
                      </div>
                    </div>
                  </div>
                )}
              </CascadeCard>
            ) : (
              <div className="flex actr jctr w100">
                <NoDataCard
                  imageURL={`url(${emptyState?.nodataImage ||
                    `${envConfig.imageRoot}svg/not-found.svg`})`}
                  text={
                    emptyState?.heading || (
                      <FormattedMessage id="app.selectionsheet.emptyState" />
                    )
                  }
                  description={emptyState?.description}
                />
              </div>
            )}
          </BoxContainerWrapper>
        </div>
      </Page>
    </div>
  );
}

function Cause(props) {
  const { name } = props.item;
  const {
    updateCause,
    item,
    updateSubCause,
    parentItem,
    isSelected,
    deleteCause,
    setSelectedCause,
    disableSort
  } = props;
  const [edit, setEdit] = useState(false);

  const handleFocusOut = (text) => {
    if (text) {
      if (updateCause) {
        updateCause(item, text || item.name, name);
      } else {
        updateSubCause(item, parentItem, text || item.name, name);
      }
    }
    setEdit(false);
  };
  return edit ? (
    <InlineEdit
      text={name}
      labelClassName={'InlineEditLabel'}
      inputClassName="InlineEditInput"
      labelFontWeight="bold"
      inputFontWeight="bold"
      onFocus={() => {}}
      inputClassName="category-inline-edit"
      isEditing
      onFocusOut={handleFocusOut}
    />
  ) : (
    <div className="hiddenContainer">
      <ResourceItem id={name} accessibilityLabel={`View details for ${name}`}>
        <div className="flex g5">
          {!disableSort && (
            <AccessControl
              roleToCheck="customFields.edit"
              render={<SuperSortable.DragHandle className="self-center" />}
            />
          )}

          <div className="w100">
            <div className="fw flex resourceItem">
              <div className="w-90" onClick={() => setSelectedCause(item)}>
                <TextStyle variation="strong">{name}</TextStyle>
              </div>
              <ActionIcons className="flex jend actions">
                <AccessControl
                  roleToCheck="customFields.edit"
                  render={
                    <div
                      className="hiddenActions mr-10"
                      onClick={(e) => {
                        setEdit(true);
                        // e.preventDefault();
                      }}
                    >
                      <SVGIcon name="Edit" />
                    </div>
                  }
                />
                <AccessControl
                  roleToCheck="customFields.edit"
                  render={
                    <div
                      className="hiddenActions"
                      onClick={() => deleteCause(item)}
                    >
                      <SVGIcon name="Delete" />
                    </div>
                  }
                />
              </ActionIcons>
            </div>
          </div>
        </div>
      </ResourceItem>
    </div>
  );
}

const SubcauseItem = (props) => {
  const { name, deleteCause } = props.item;
  const description = props.item?.details?.description || null;
  const {
    intl,
    updateCause,
    item,
    updateSubCause,
    parentItem,
    index,
    editItem,
    onChange,
    disableSort
  } = props;
  const [edit, setEdit] = useState(false);

  const onSuccessHandler = (updatedObj) => {
    const { name: text, description: desc } = updatedObj;
    if (updateCause) {
      updateCause(item, text || item.name, name);
    } else {
      updateSubCause(item, parentItem, text || item.name, name, desc);
    }
    setEdit(false);
    setEdit(false);
    if (props?.onEditCancel) {
      props.onEditCancel(null);
    }
  };

  const onCloseHandler = () => {
    setEdit(false);
    if (props?.onEditCancel) {
      props.onEditCancel(null);
    }
  };

  const onEditClick = (e) => {
    e.preventDefault();
    if (props?.onEdit) {
      props.onEdit(item, index);
    }
  };

  const editItemData = editItem?.item;
  useEffect(() => {
    if (editItem?.item?.id === item?.id) {
      setEdit(true);
    } else {
      setEdit(false);
    }
  }, [editItem, item]);

  return edit ? (
    <SubcauseInput
      intl={intl}
      selectedCauseItem={parentItem}
      onSuccess={onSuccessHandler}
      onClose={onCloseHandler}
      item={{
        name: editItemData?.name || name,
        details: { description: editItemData?.description || description }
      }}
      childrenPlaceholderText={props.childrenPlaceholderText}
      mode="edit"
      onChange={onChange}
      disableSort={disableSort}
    />
  ) : (
    <div className="hiddenContainer w100">
      <ResourceItem id={name} accessibilityLabel={`View details for ${name}`}>
        <div className="flex g5">
          {!disableSort && (
            <AccessControl
              roleToCheck="customFields.edit"
              render={<SuperSortable.DragHandle className="self-center" />}
            />
          )}

          <div className="w100">
            <div>
              <div className="fw flex resourceItem">
                <TextStyle variation="strong">{name}</TextStyle>
                <ActionIcons className="flex jend actions">
                  <AccessControl
                    roleToCheck="customFields.edit"
                    render={
                      <div
                        className="hiddenActions mr-10"
                        onClick={(e) => {
                          onEditClick(e);
                        }}
                      >
                        <SVGIcon name="Edit" />
                      </div>
                    }
                  />
                  <AccessControl
                    roleToCheck="customFields.edit"
                    render={
                      <div
                        className="hiddenActions"
                        onClick={(e) => {
                          props.deleteCause();
                          e.preventDefault();
                        }}
                      >
                        <SVGIcon name="Delete" />
                      </div>
                    }
                  />
                </ActionIcons>
              </div>
            </div>
            <div>
              <TextStyle variation="subdued">
                {description ||
                  getTranslatedText(intl, 'admin.CauseAndSubcause.commonPL')}
              </TextStyle>
            </div>
          </div>
        </div>
      </ResourceItem>
    </div>
  );
};

export default injectIntl(CauseCascade);
